import React, { useState, useEffect } from 'react'
import Typography from 'aldoo-ra/Typography'
import Button from 'aldoo-ra/Button'
import DataTable from 'aldoo-ra/DataTable'
import Checkbox from 'aldoo-ra/Checkbox'
import Badge from 'aldoo-ra/Badge'
import Tooltip from 'aldoo-ra/Tooltip'
import { showNotification } from 'aldoo-ra/Notification'
import { showPromptDialog } from 'aldoo-ra/PromptDialog'
import { GetCMSContent, DeleteCMSContent, SaveCMSContent, BulkGetCMSContent } from 'aldoo-ra/CMS/api'
import {
  PlusCircleIcon,
  TrashIcon,
  PencilIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  ArrowUturnLeftIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline'
import AldooPageDialog from './AldooPageDialog'
import AddNewPage from './AddNewPage'
// import PageEditor from 'aldoo-ra/PageEditor'
import PageEditor from 'aldoo-ra/aldoo-vbecs/VBECSEditor'
import RestoreDialog from 'aldoo-ra/CMS/dialogs/restore-dialog'
import ImagePreview from 'aldoo-ra/CMS/preview/image-preview'
import { url } from 'aldoo-ra/CMS/url'
import { useRealtime } from 'aldoo-ra/Realtime/hooks'

// Page Card Component
const PageCard = ({ page, onEdit, onDelete, screenshotVersion }) => {
  const [showFullPreview, setShowFullPreview] = useState(false)
  const previewImage = page.content?.preview_image
  const pageName = page.content?.name || 'Untitled Page'
  const status = page.content?.status || 'draft'

  const getStatusVariant = (status) => {
    switch (status) {
      case 'published':
        return 'admin_success'
      case 'draft':
        return 'admin_neutral'
      case 'scheduled':
        return 'admin_info'
      default:
        return 'admin_neutral'
    }
  }

  return (
    <>
      <div
        className="relative group cursor-pointer"
        onClick={() => onEdit(page)}
      >
        {/* Card */}
        <div className="relative w-full aspect-[3/4] bg-admin_canvas border border-admin_bordered rounded-2xl overflow-hidden hover:border-admin_primary transition-all duration-200">
          {/* Preview Image */}
          <div className="w-full h-full flex items-center justify-center bg-admin_neutral_1/20">
            {previewImage ? (
              <img
                src={`${url(previewImage)}?v=${screenshotVersion || 0}`}
                alt={pageName}
                className="w-full h-full object-cover"
                key={`${page._id}-${screenshotVersion || 0}`}
              />
            ) : (
              <DocumentTextIcon className="w-20 h-20 text-admin_text_secondary opacity-30" />
            )}
          </div>

          {/* Status Indicator */}
          {status === 'published' && (
            <div className="absolute top-3 right-3">
              <div className="w-3 h-3 rounded-full bg-green-500" />
            </div>
          )}

          {/* Delete Button Overlay */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              onDelete(page)
            }}
            className="absolute bottom-3 left-3 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            title="Delete page"
          >
            <TrashIcon className="w-4 h-4 text-white" />
          </button>

          {/* Screenshot Preview Button Overlay */}
          {previewImage && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowFullPreview(true)
              }}
              className="absolute bottom-3 right-3 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              title="View screenshot"
            >
              <PhotoIcon className="w-4 h-4 text-white" />
            </button>
          )}
        </div>

        {/* Page Name */}
        <div className="mt-3 px-1 text-center">
          <Typography variant="admin_label" className="truncate">
            {pageName}
          </Typography>
        </div>
      </div>

      {/* Full Screenshot Preview Modal */}
      {showFullPreview && previewImage && (
        <ImagePreview
          isOpen={showFullPreview}
          onClose={() => setShowFullPreview(false)}
          imageUrl={url(previewImage)}
          fileName={pageName}
          mode="light"
          title={pageName}
          defaultZoom={0.6}
        />
      )}
    </>
  )
}

const Pages = () => {
  const [refreshKey, setRefreshKey] = useState(0)
  const [selectedItems, setSelectedItems] = useState([])
  const [showDialog, setShowDialog] = useState(false)
  const [showAddNewPage, setShowAddNewPage] = useState(false)
  const [showPageEditor, setShowPageEditor] = useState(false)
  const [showRestoreDialog, setShowRestoreDialog] = useState(false)
  const [editingPage, setEditingPage] = useState(null)
  const [screenshotUpdates, setScreenshotUpdates] = useState({})

  // Connect to CMS realtime namespace for screenshot updates
  const { socket, connected } = useRealtime('cms')

  // Listen for screenshot updates
  useEffect(() => {
    if (!socket || !connected) return

    const handleScreenshotUpdate = (data) => {
      const { pageId } = data
      // Update only the specific page's screenshot cache-buster
      setScreenshotUpdates(prev => ({
        ...prev,
        [pageId]: Date.now()
      }))
    }

    socket.on('cms:screenshot_updated', handleScreenshotUpdate)

    return () => {
      socket.off('cms:screenshot_updated', handleScreenshotUpdate)
    }
  }, [socket, connected])

  // Check URL on mount to see if we should open a specific page
  useEffect(() => {
    const checkUrlForPageId = async () => {
      const path = window.location.pathname
      const match = path.match(/\/admin\/structure\/page\/([^/]+)/)
      if (match) {
        const pageId = match[1]
        try {
          // Try fetching by content.id first (the unique page ID)
          let response = await GetCMSContent({
            match: { 'content.id': pageId },
            itemsPerPage: 1,
          })

          // If not found, try fetching by MongoDB _id as fallback
          if (!response.result || !response.data || response.data.length === 0) {
            response = await GetCMSContent({
              match: { _id: pageId },
              itemsPerPage: 1,
            })
          }

          if (response.result && response.data && response.data.length > 0) {
            const page = response.data[0]
            setEditingPage(page)
            setShowPageEditor(true)
          }
        } catch (error) {
          console.error('Error loading page from URL:', error)
        }
      }
    }
    checkUrlForPageId()
  }, [])

  // API method to get pages from CMS
  const getPages = async (requestFields = {}) => {
    try {
      const response = await GetCMSContent({
        ...requestFields,
        match: {
          'content.type': 'AldooPage',
          ...requestFields.match
        }
      })

      if (!response.result) {
        return { result: false, data: [], totalItems: 0 }
      }

      // Collect all IDs to fetch
      const allLayoutIds = []
      const allLinkIds = []

      response.data.forEach(page => {
        // Collect layout/slot IDs
        if (page.content?.layout && Array.isArray(page.content.layout)) {
          page.content.layout.forEach(ref => {
            if (ref._id) allLayoutIds.push(ref._id)
          })
        }
        // Collect link IDs
        if (page.content?.link && Array.isArray(page.content.link) && page.content.link.length > 0) {
          const linkRef = page.content.link[0]
          if (linkRef._id) allLinkIds.push(linkRef._id)
        }
      })

      // Bulk fetch all layouts/slots
      let layoutsMap = {}
      if (allLayoutIds.length > 0) {
        const layoutsResponse = await BulkGetCMSContent({
          payload: { ids: allLayoutIds, expand: true }
        })
        if (layoutsResponse.result && layoutsResponse.data) {
          layoutsMap = Object.fromEntries(
            layoutsResponse.data.map(item => [item._id, item.content])
          )
        }
      }

      // Bulk fetch all links
      let linksMap = {}
      if (allLinkIds.length > 0) {
        const linksResponse = await BulkGetCMSContent({
          payload: { ids: allLinkIds, expand: true }
        })
        if (linksResponse.result && linksResponse.data) {
          linksMap = Object.fromEntries(
            linksResponse.data.map(item => [item._id, item.content])
          )
        }
      }

      // Populate layout and link references using fetched data
      const populatedData = response.data.map(page => {
        // Populate layouts from map
        if (page.content?.layout && Array.isArray(page.content.layout) && page.content.layout.length > 0) {
          page.content.layout = page.content.layout.map(layoutRef => {
            const layoutContent = layoutsMap[layoutRef._id]
            if (layoutContent) {
              return {
                ...layoutRef,
                content: layoutContent
              }
            }
            return layoutRef
          })
        }

        // Populate link from map
        if (page.content?.link && Array.isArray(page.content.link) && page.content.link.length > 0) {
          const linkRef = page.content.link[0]
          const linkContent = linksMap[linkRef._id]
          if (linkContent) {
            page.content.link = [{
              ...linkRef,
              content: linkContent
            }]
          }
        }

        return page
      })

      return {
        result: true,
        data: populatedData,
        totalItems: response.totalItems || populatedData.length
      }
    } catch (error) {
      console.error('Failed to fetch pages:', error)
      return { result: false, data: [], totalItems: 0 }
    }
  }

  // Handle select all
  const handleSelectAll = (items, isSelected) => {
    if (isSelected) {
      setSelectedItems(items.map(item => item._id))
    } else {
      setSelectedItems([])
    }
  }

  // Handle individual selection
  const handleSelectItem = (itemId, isSelected) => {
    if (isSelected) {
      setSelectedItems(prev => [...prev, itemId])
    } else {
      setSelectedItems(prev => prev.filter(id => id !== itemId))
    }
  }

  // Handle bulk delete
  const handleDeleteSelected = () => {
    if (selectedItems.length === 0) return

    showPromptDialog({
      title: 'Delete Pages',
      message: `Are you sure you want to delete ${selectedItems.length} page${selectedItems.length === 1 ? '' : 's'}? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmVariant: 'error_m',
      onConfirm: async () => {
        try {
          let deletedCount = 0
          for (const id of selectedItems) {
            const response = await DeleteCMSContent({ itemId: id })
            if (response.result) deletedCount++
          }

          showNotification({
            message: `Deleted ${deletedCount} page${deletedCount === 1 ? '' : 's'}`,
            className: 'bg-admin_primary text-admin_text_inv'
          })

          setSelectedItems([])
          setRefreshKey(prev => prev + 1)
        } catch (error) {
          showNotification({
            message: 'Failed to delete pages',
            className: 'bg-admin_error text-white'
          })
        }
      }
    })
  }

  // Handle edit page
  const handleEditPage = (page) => {
    setEditingPage(page)
    setShowPageEditor(true)
    // Update URL to include page ID (use content.id which is the unique page ID)
    const pageId = page.content?.id || page.id || page._id
    if (pageId) {
      window.history.pushState({}, '', `/admin/structure/page/${pageId}`)
    }
  }

  // Handle create page (simple)
  const handleCreatePage = () => {
    setShowAddNewPage(true)
  }

  // Handle page created
  const handlePageCreated = (pageData) => {
    setRefreshKey(prev => prev + 1)
  }

  // Handle delete page
  const handleDeletePage = (page) => {
    showPromptDialog({
      title: 'Delete Page',
      message: `Are you sure you want to delete page "${page.content?.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmVariant: 'error_m',
      onConfirm: async () => {
        try {
          const response = await DeleteCMSContent({ itemId: page._id })

          if (response.result || response.error === undefined) {
            showNotification({
              message: `Page "${page.content?.name}" deleted successfully`,
              className: 'bg-admin_primary text-admin_text_inv'
            })
            setRefreshKey(prev => prev + 1)
          } else {
            showNotification({
              message: response.error || 'Failed to delete page',
              className: 'bg-admin_error text-white'
            })
          }
        } catch (error) {
          showNotification({
            message: 'An error occurred while deleting the page',
            className: 'bg-admin_error text-white'
          })
        }
      }
    })
  }

  // Handle dialog close
  const handleDialogClose = () => {
    setShowDialog(false)
    setEditingPage(null)
    setRefreshKey(prev => prev + 1)
  }

  // Handle page editor close
  const handlePageEditorClose = (result) => {
    setShowPageEditor(false)
    setEditingPage(null)
    // Reset URL back to structure page
    window.history.pushState({}, '', '/admin/structure')
    // Refresh the list if a page was deleted
    if (result?.deleted) {
      setRefreshKey(prev => prev + 1)
    }
  }

  // Handle page editor save
  const handlePageEditorSave = (response) => {
    setRefreshKey(prev => prev + 1)
  }

  // Controller for restore dialog
  const restoreController = {
    restoreContent: async (id) => {
      try {
        const response = await SaveCMSContent({
          payload: {
            _id: id,
            deleted: false,
          }
        })
        if (response.result) {
          setRefreshKey(prev => prev + 1)
        }
        return response
      } catch (error) {
        console.error('Failed to restore page:', error)
        return { error: error.message }
      }
    },
    deleteContent: async (id, mode) => {
      try {
        const response = await DeleteCMSContent({ itemId: id, mode })
        if (response.result || !response.error) {
          setRefreshKey(prev => prev + 1)
        }
        return response
      } catch (error) {
        console.error('Failed to delete page:', error)
        return { error: error.message }
      }
    },
    onRefreshContents: (callback) => {
      callback()
    }
  }

  // Get status badge variant
  const getStatusVariant = (status) => {
    switch (status) {
      case 'published':
        return 'admin_success'
      case 'draft':
        return 'admin_neutral'
      case 'scheduled':
        return 'admin_info'
      default:
        return 'admin_neutral'
    }
  }

  // Custom grid renderer for individual card
  const renderGridRow = (page, index) => {
    return (
      <PageCard
        key={page._id}
        page={page}
        onEdit={handleEditPage}
        onDelete={handleDeletePage}
        screenshotVersion={screenshotUpdates[page._id]}
      />
    )
  }

  // Table footer
  const tableFooter = selectedItems.length > 0 ? (
    <div className="flex items-center justify-between p-4 border-t border-admin_bordered">
      <Typography variant="admin_body">
        {selectedItems.length} page{selectedItems.length !== 1 ? 's' : ''} selected
      </Typography>

      <div className="flex items-center gap-2">
        <Button variant="admin_secondary" onClick={() => setSelectedItems([])}>
          Deselect
        </Button>
        <Button variant="admin_error" onClick={handleDeleteSelected}>
          <TrashIcon className="w-4 h-4 mr-1" />
          Delete
        </Button>
      </div>
    </div>
  ) : null

  return (
    <div className="p-6">
      <div className="w-full flex items-center justify-between mb-5">
        <Typography component="h1" variant="admin_h3">
          Pages
        </Typography>

        <div className="flex items-center gap-2">
          <Button
            variant="admin_secondary"
            onClick={() => setShowRestoreDialog(true)}
          >
            <ArrowUturnLeftIcon className="w-4 h-4 mr-1" />
            Restore
          </Button>
          <Button
            variant="admin_primary"
            onClick={handleCreatePage}
          >
            <PlusCircleIcon className="w-4 h-4 mr-1" />
            Add Page
          </Button>
        </div>
      </div>

      <DataTable
        variant="admin_browser"
        apiMethod={getPages}
        requestFields={{}}
        tableFooter={tableFooter}
        searchField="content.name"
        itemsPerPage={24}
        refreshKey={refreshKey}
        preferredHeight="70vh"
        localUserPreferencesKey="admin.structure.pages"
        renderRow={renderGridRow}
        styleOverrides={{
          table: 'w-full',
          header: 'w-full flex justify-between items-end gap-2 mt-8 mb-4',
          container: 'relative w-full flex flex-col h-[90vh] justify-start bg-admin_paper rounded-3xl px-6 pt-8 pb-6',
          searchInput: 'absolute w-[350px] h-[30px] top-6 right-4 text-text bg-transparent outline-none flex-1 px-2 py-1',
          tbody: 'w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-6 p-6'
        }}
        noData={
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📄</div>
            <Typography variant="admin_h4" className="text-admin_text_secondary mb-2">
              No pages found
            </Typography>
            <Typography variant="admin_txt1_dim" className="mb-4">
              Create a new page to get started
            </Typography>
            <Button variant="admin_primary" onClick={handleCreatePage}>
              <PlusCircleIcon className="w-4 h-4 mr-1" />
              Create Page
            </Button>
          </div>
        }
      />

      {showAddNewPage && (
        <AddNewPage
          isOpen={showAddNewPage}
          onClose={() => setShowAddNewPage(false)}
          onPageCreated={handlePageCreated}
        />
      )}

      {showDialog && (
        <AldooPageDialog
          isOpen={showDialog}
          onClose={handleDialogClose}
          page={editingPage}
        />
      )}

      {showPageEditor && editingPage && (
        <PageEditor
          page={editingPage}
          onSave={handlePageEditorSave}
          onClose={handlePageEditorClose}
        />
      )}

      {showRestoreDialog && (
        <RestoreDialog
          controller={restoreController}
          isOpen={showRestoreDialog}
          onClose={() => setShowRestoreDialog(false)}
          type="content"
          additionalFilters={{ 'content.type': 'AldooPage' }}
        />
      )}
    </div>
  )
}

export default Pages